import React, { forwardRef, useImperativeHandle, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { CaptchaPolicy } from "../../../captcha/captcha-policy";
import { useRevalidateFormOnChange } from "../../../hooks/use-revalidate-form-on-change";
import { SpecialContentHandler } from "../../strips/special-content/types";
import { FormFieldStatus, Input } from "../elements";
import { AddressFormFields, AddressFormFieldsProps } from "../forms-fields/address-form-fields";
import { EmailMessageFormFields } from "../forms-fields/email-message-form-fields";
import {
  MaterialsApiFormFields,
  MaterialsApiFormFieldsProps,
} from "../forms-fields/materials-api-form-fields";
import { PersonalDataFormFields } from "../forms-fields/personal-data-form-fields";
import { PrivacyFormFields, PrivacyFormFieldsProps } from "../forms-fields/privacy-form-fields";
import {
  ProcessOfInterestFieldsProps,
  ProcessOfInterestFormFields,
} from "../forms-fields/process-of-interest-form-fields/form-fields";
import { emptyRequestInfoFormValues } from "./constants";
import { SubmitButton } from "./submit-button";
import { RequestInfoFormLabels, RequestInfoFormTypes } from "./types";

export interface RequestInfoFormProps extends Pick<PrivacyFormFieldsProps, "consents"> {
  labels: RequestInfoFormLabels;
  onSubmit: (values: RequestInfoFormTypes) => void;
  countriesList: AddressFormFieldsProps["countriesList"];
  statesList: AddressFormFieldsProps["statesList"];
  materialsList: MaterialsApiFormFieldsProps["optionsList"];
  processOfInterestList?: ProcessOfInterestFieldsProps["optionsList"];
  defaultValues?: Partial<RequestInfoFormTypes>;
  isRecaptchaNotReady?: boolean;
  type:
    | SpecialContentHandler.NewsletterForm
    | SpecialContentHandler.RequestDemoForm
    | SpecialContentHandler.RequestInfoForm
    | SpecialContentHandler.RequestSupportForm;
}

export interface RequestInfoFormHandle {
  resetForm: (values?: Partial<RequestInfoFormTypes>) => void;
}

export const RequestInfoForm = forwardRef<RequestInfoFormHandle, RequestInfoFormProps>(
  (
    {
      labels,
      consents,
      onSubmit,
      defaultValues,
      type,
      materialsList,
      statesList,
      countriesList,
      processOfInterestList,
      isRecaptchaNotReady = false,
    },
    ref
  ) => {
    const formMethods = useForm<RequestInfoFormTypes>({
      mode: "onBlur",
      reValidateMode: "onChange",
      defaultValues: {
        ...emptyRequestInfoFormValues,
        ...defaultValues,
      },
    });

    const {
      formState: { isValid: isFormValid },
    } = formMethods;

    useRevalidateFormOnChange(formMethods);
    const isSubmitDisabled = useMemo(
      () => isRecaptchaNotReady || !isFormValid,
      [isRecaptchaNotReady, isFormValid]
    );

    const consentsDefaults = useMemo(
      () =>
        consents?.reduce<RequestInfoFormTypes["consents"]>(
          (acc, consent) => ({
            ...acc,
            // On biesse request, this particular case must be considered checked on default
            [consent.consentId]:
              type === SpecialContentHandler.NewsletterForm && consent.consentId === "marketing",
          }),
          {}
        ),
      [consents, type]
    );

    useImperativeHandle(
      ref,
      () => ({
        resetForm: (values?: Partial<RequestInfoFormTypes>) => {
          formMethods?.reset({
            ...emptyRequestInfoFormValues,
            consents: { ...consentsDefaults },
            ...values,
          });
        },
      }),
      [consentsDefaults, formMethods]
    );

    const { errors } = formMethods.formState;

    return (
      <form className="w-full" onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormProvider {...formMethods}>
          <div className="w-full flex flex-col gap-8">
            <PersonalDataFormFields labels={labels} isPhoneRequired />
            <MaterialsApiFormFields labels={labels} optionsList={materialsList} />
            <AddressFormFields
              labels={labels}
              countriesList={countriesList}
              statesList={statesList}
            />
            {type !== SpecialContentHandler.NewsletterForm && (
              <EmailMessageFormFields labels={labels} isRequired />
            )}
            {type === SpecialContentHandler.RequestSupportForm && (
              <Input
                className="col-span-6 lg:col-span-12"
                label={labels.machineSerialNumber}
                required={false}
                message={errors.machineSerialNumber?.message}
                status={FormFieldStatus.Error}
                optionalLabel={labels.optionalLabel}
                {...formMethods?.register("machineSerialNumber")}
              />
            )}
            {type === SpecialContentHandler.RequestDemoForm && processOfInterestList && (
              <ProcessOfInterestFormFields optionsList={processOfInterestList} labels={labels} />
            )}

            <PrivacyFormFields
              labels={labels}
              consents={consents}
              hideMarketing={type === SpecialContentHandler.NewsletterForm}
            />
            {/* Submit button */}
            <div className="flex flex-col gap-1 items-start">
              <SubmitButton disabled={isSubmitDisabled}>{labels.submit}</SubmitButton>
              <CaptchaPolicy />
            </div>
          </div>
        </FormProvider>
      </form>
    );
  }
);
